import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Mic, 
  Radio, 
  Users, 
  Headphones, 
  Play,
  Trophy,
  Lightbulb,
  ChevronRight,
  ArrowRight,
  Newspaper,
  Clock,
  Calendar,
  Eye,
  Heart,
  Star,
  Sparkles,
  GraduationCap,
  Music,
  Megaphone
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePodcasts } from '../hooks/usePodcasts';

const Home = () => {
  const [featuredPodcasts, setFeaturedPodcasts] = useState([]);
  const { fetchPodcasts } = usePodcasts();

  useEffect(() => {
    const loadFeaturedPodcasts = async () => {
      try {
        const allPodcasts = await fetchPodcasts();
        // Get the 3 most played podcasts
        const featured = allPodcasts
          .sort((a, b) => (b.plays || 0) - (a.plays || 0))
          .slice(0, 3);
        setFeaturedPodcasts(featured);
      } catch (error) {
        console.error('Error loading featured podcasts:', error);
      }
    };

    loadFeaturedPodcasts();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, 150]);
  const y2 = useTransform(scrollY, [0, 500], [0, -150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const parallaxImages = [
    "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80",
    "https://images.unsplash.com/photo-1516280440614-37939bbacd81?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80",
    "https://images.unsplash.com/photo-1519682337058-a94d519337bc?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % parallaxImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const stats = [
    { icon: Headphones, value: '1,200+', label: 'Ouvintes Mensais' },
    { icon: Radio, value: '50+', label: 'Episódios Publicados' },
    { icon: Users, value: '10+', label: 'Colaboradores' },
    { icon: Heart, value: '95%', label: 'Taxa de Satisfação' }
  ];

  const featuredPodcastsData = [
    {
      title: 'Entrevista com Alunos do Curso de Multimédia',
      category: 'Educação',
      image: 'https://images.unsplash.com/photo-1542744094-3a31f272c490?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      duration: '25:30',
      views: 156
    },
    {
      title: 'Projeto Escola Verde - Iniciativas Ambientais',
      category: 'Projetos',
      image: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      duration: '18:45',
      views: 98
    },
    {
      title: 'Arte e Cultura na Escola',
      category: 'Cultura',
      image: 'https://images.unsplash.com/photo-1460518451285-97b6aa326961?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      duration: '22:15',
      views: 167
    }
  ];

  return (
    <div className="min-h-screen">
      <div className="grid-overlay">
        <div className="grid-background w-full h-full" />
      </div>

      <div className="relative h-screen overflow-hidden">
        {parallaxImages.map((image, index) => (
          <motion.div
            key={image}
            className="absolute inset-0 w-full h-full"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: currentImageIndex === index ? 1 : 0,
              scale: currentImageIndex === index ? 1.1 : 1
            }}
            transition={{ duration: 1.5 }}
            style={{ y: y1 }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/40 to-background z-10" />
            <img
              src={image}
              alt="Background"
              className="w-full h-full object-cover"
            />
          </motion.div>
        ))}

        <div className="absolute inset-0 w-full h-full" style={{ opacity: 0.6 }}>
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/40 to-background z-10" />
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="https://assets.mixkit.co/videos/preview/mixkit-recording-a-podcast-in-a-professional-studio-44645-large.mp4" type="video/mp4" />
          </video>
        </div>

        <motion.div 
          className="absolute inset-0 z-20"
          style={{ y: y2 }}
        >
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-primary/10 rounded-full blur-3xl" />
        </motion.div>

        <motion.div 
          className="relative z-30 h-full flex items-center justify-center px-4"
          style={{ opacity }}
        >
          <div className="text-center max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-5xl md:text-7xl font-bold mb-6 flex items-center justify-center"
            >
              <motion.span
                className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-primary/80 to-primary/60"
                animate={{ 
                  backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                }}
                transition={{ 
                  duration: 5,
                  repeat: Infinity,
                  ease: "linear"
                }}
                style={{
                  backgroundSize: '200% auto',
                }}
              >
                SIN
              </motion.span>
              <motion.div
                className="mx-1"
                animate={{ 
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                <Mic className="w-12 h-12 text-primary animate-pulse" />
              </motion.div>
              <motion.span
                className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-primary/80 to-primary/60"
                animate={{ 
                  backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                }}
                transition={{ 
                  duration: 5,
                  repeat: Infinity,
                  ease: "linear"
                }}
                style={{
                  backgroundSize: '200% auto',
                }}
              >
                PSE
              </motion.span>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-xl md:text-2xl mb-8 text-foreground/90 drop-shadow-lg"
            >
              A Voz do Agrupamento de Escolas de Albergaria a Velha
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/podcasts"
                className="group flex items-center justify-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors backdrop-blur-sm"
              >
                <Play className="w-5 h-5" />
                Começar a Ouvir
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30"
          style={{ opacity }}
        >
          <motion.div
            animate={{
              y: [0, 10, 0],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            className="w-6 h-10 border-2 border-foreground/20 rounded-full flex justify-center backdrop-blur-sm"
          >
            <motion.div className="w-1 h-2 bg-primary rounded-full mt-2" />
          </motion.div>
        </motion.div>
      </div>

      <div className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/20 to-background" />
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative max-w-7xl mx-auto px-4"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="bg-card p-6 rounded-lg relative group hover:ring-2 hover:ring-primary/50 transition-all"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent rounded-lg" />
                  <stat.icon className="w-8 h-8 text-primary mx-auto mb-4" />
                  <motion.h3
                    className="text-3xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60 bg-[length:200%_auto]"
                    initial={{ backgroundPosition: '0% 50%' }}
                    whileHover={{ backgroundPosition: '100% 50%' }}
                  >
                    {stat.value}
                  </motion.h3>
                  <p className="text-gray-400">{stat.label}</p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/20 to-background" />
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative max-w-7xl mx-auto px-4"
        >
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-center justify-center gap-3 mb-4"
            >
              <Star className="w-8 h-8 text-primary" />
              <h2 className="text-3xl font-bold">Podcasts em Destaque</h2>
            </motion.div>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Descubra os episódios mais populares e mergulhe em conversas inspiradoras sobre educação, cultura e inovação.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {featuredPodcasts.map((podcast, index) => (
              <motion.div
                key={podcast.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="group"
              >
                <div className="bg-card rounded-lg overflow-hidden hover:ring-2 hover:ring-primary/50 transition-all">
                  <div className="relative h-48 overflow-hidden">
                    <motion.img
                      src={podcast.image_url}
                      alt={podcast.title}
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.3 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <Link
                      to={`/podcasts/${podcast.slug}`}
                      className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <div className="p-4 bg-primary text-primary-foreground rounded-full">
                        <Play className="w-6 h-6" />
                      </div>
                    </Link>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-3 py-1 bg-secondary rounded-full text-sm">
                        {podcast.category?.name}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold mb-4 line-clamp-2 group-hover:text-primary transition-colors">
                      {podcast.title}
                    </h3>
                    <div className="flex items-center justify-between text-sm text-gray-400">
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {podcast.duration}
                      </span>
                      <span className="flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        {podcast.plays} plays
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/podcasts"
              className="inline-flex items-center gap-2 px-8 py-3 bg-secondary hover:bg-secondary/80 rounded-full transition-colors group"
            >
              Ver Todos os Episódios
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </motion.div>
      </div>

      <div className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/20 to-background" />
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative max-w-7xl mx-auto px-4"
        >
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-center justify-center gap-3 mb-4"
            >
              <Sparkles className="w-8 h-8 text-primary" />
              <h2 className="text-3xl font-bold">Explore por Categoria</h2>
            </motion.div>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Da educação à cultura, encontre conteúdo relevante em diferentes áreas.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { icon: Radio, name: 'Últimos Episódios', path: '/podcasts/latest' },
              { icon: Lightbulb, name: 'Projetos Escolares', path: '/podcasts/projects' },
              { icon: Megaphone, name: 'Notícias', path: '/podcasts/news' },
              { icon: Music, name: 'Arte e Cultura', path: '/podcasts/culture' },
              { icon: GraduationCap, name: 'Educação', path: '/podcasts/education' },
              { icon: Trophy, name: 'Desporto Escolar', path: '/podcasts/sports' }
            ].map((category, index) => (
              <motion.div
                key={category.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={category.path}
                  className="block group"
                >
                  <motion.div
                    whileHover={{ y: -5 }}
                    className="bg-card p-6 rounded-lg text-center hover:ring-2 hover:ring-primary/50 transition-all relative overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <category.icon className="w-8 h-8 text-primary mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    <h3 className="text-sm font-medium group-hover:text-primary transition-colors">
                      {category.name}
                    </h3>
                  </motion.div>
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/20 to-background" />
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative max-w-7xl mx-auto px-4"
        >
          <div className="bg-card rounded-2xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent" />
            <div className="relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="max-w-3xl mx-auto text-center"
              >
                <h2 className="text-3xl font-bold mb-4">Faça Parte do SINOPSE</h2>
                <p className="text-gray-400 mb-8">
                  Quer contribuir com as suas ideias? Tem uma história para contar? 
                  Junte-se a nós e faça parte desta comunidade vibrante!
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors group"
                >
                  Participar
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Home;